import { useCallback, useEffect, useState } from 'react';
import { Badge, Box, Button, Flex, Grid, Input, InputGroup, InputLeftElement, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { FaBullhorn, FaPlus, FaSearch } from 'react-icons/fa';
import { ArteInformativo } from '../../components/ArteInformativo';
import DashLayout from '../../components/DashLayout';
import { EmptyState } from '../../components/EmptyState';
import { Loading } from '../../components/Loading';
import { PageHeader } from '../../components/PageHeader';
import { InformativoGet, listarInformativos } from '../../services/api';
import { formatarData } from '../../utils/data';
import { DrawerInformativo, TIPOS } from './DrawerInformativo';

type Situacao = { rotulo: string; cor: string };

export const rotuloDoTipo = (tipo: InformativoGet['tipo']) =>
	TIPOS.find((item) => item.valor === tipo)?.rotulo ?? tipo;

/**
 * O que o admin precisa saber batendo o olho: um informativo pode estar
 * despublicado, esperando a data de início ou já vencido, e nos três casos ele
 * some do portal sem nada indicar o motivo na lista.
 */
export const situacaoDoInformativo = (informativo: InformativoGet, agora = new Date()): Situacao => {
	if (!informativo.ativo) return { rotulo: 'Despublicado', cor: 'gray' };
	if (informativo.inicioEm && new Date(informativo.inicioEm) > agora) return { rotulo: 'Agendado', cor: 'purple' };
	if (informativo.fimEm && new Date(informativo.fimEm) < agora) return { rotulo: 'Expirado', cor: 'orange' };
	return { rotulo: 'No mural', cor: 'green' };
};

const periodo = (informativo: InformativoGet) => {
	const inicio = formatarData(informativo.inicioEm);
	const fim = formatarData(informativo.fimEm);

	if (inicio && fim) return `De ${inicio} até ${fim}`;
	if (inicio) return `A partir de ${inicio}`;
	if (fim) return `Até ${fim}`;
	return 'Sem data para sair do mural';
};

export const Informativos = () => {
	const toast = useToast();
	const drawer = useDisclosure();

	const [ informativos, setInformativos ] = useState<InformativoGet[]>([]);
	const [ selecionado, setSelecionado ] = useState<InformativoGet | undefined>();
	const [ search, setSearch ] = useState('');
	const [ loading, setLoading ] = useState(true);

	const buscarInformativos = useCallback(
		() => {
			setLoading(true);
			listarInformativos()
				.then((response) => {
					setInformativos(response.data);
					setLoading(false);
				})
				.catch((error) => {
					if (!error.response) return;
					setLoading(false);
					toast({
						title: 'Não foi possível listar os informativos.',
						status: 'error',
						isClosable: true
					});
				});
		},
		[ toast ]
	);

	useEffect(
		() => {
			buscarInformativos();
		},
		[ buscarInformativos ]
	);

	const abrirNovo = () => {
		setSelecionado(undefined);
		drawer.onOpen();
	};

	const abrirEdicao = (informativo: InformativoGet) => {
		setSelecionado(informativo);
		drawer.onOpen();
	};

	const encontrados = informativos.filter((informativo) =>
		`${informativo.titulo} ${rotuloDoTipo(informativo.tipo)}`
			.toLowerCase()
			.includes(search.toLowerCase())
	);

	return (
		<DashLayout>
			<Flex width="100%" height="100%" flexDir="column">
				<PageHeader title="Informativos">
					<InputGroup gap="20px">
						<InputLeftElement pointerEvents="none" color="gray.600">
							<FaSearch />
						</InputLeftElement>
						<Input
							placeholder="Buscar informativos..."
							onChange={(e) => setSearch(e.target.value)}
							value={search}
							borderColor="mist"
							bgColor="paper"
						/>
						<Button leftIcon={<FaPlus />} colorScheme="blue" variant="solid" onClick={abrirNovo}>
							Novo
						</Button>
					</InputGroup>
				</PageHeader>

				<Flex width="100%" bgColor="white" minH="100%">
					{loading ? (
						<Loading />
					) : encontrados.length === 0 ? (
						<EmptyState
							icon={FaBullhorn}
							title={informativos.length === 0 ? 'Nenhum informativo publicado' : 'Nada encontrado'}
							description={
								informativos.length === 0 ? (
									'Publique um cartaz com feriados, datas comemorativas ou novidades para aparecer no portal dos alunos.'
								) : (
									'Nenhum informativo corresponde à busca.'
								)
							}
							actionLabel={informativos.length === 0 ? 'Novo informativo' : undefined}
							onAction={informativos.length === 0 ? abrirNovo : undefined}
						/>
					) : (
						<Box width="100%" overflowY="auto" padding="20px" paddingBottom="120px">
							<Grid
								templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }}
								gap="20px"
							>
								{encontrados.map((informativo) => {
									const situacao = situacaoDoInformativo(informativo);

									return (
										<Flex
											key={informativo.id}
											flexDir="column"
											borderWidth="1px"
											borderColor="mist"
											borderRadius="12px"
											overflow="hidden"
											bgColor="paper"
											cursor="pointer"
											transition="box-shadow .2s ease"
											_hover={{ boxShadow: 'md' }}
											onClick={() => abrirEdicao(informativo)}
										>
											{/* <img> e não bgImage: o Chakra descarta um
											    `url(data:...)` com vírgula, e o alt ainda dá
											    nome ao cartaz para leitores de tela. Inteira,
											    como no mural, para o admin ver o que publicou. */}
											<ArteInformativo
												src={informativo.imagemUrl}
												alt={informativo.titulo}
												height="150px"
												width="100%"
											/>
											<Flex flexDir="column" gap="6px" padding="14px">
												<Flex gap="8px" flexWrap="wrap">
													<Badge colorScheme="blue">{rotuloDoTipo(informativo.tipo)}</Badge>
													<Badge colorScheme={situacao.cor}>{situacao.rotulo}</Badge>
												</Flex>
												<Text fontWeight="bold" color="ink" noOfLines={1}>
													{informativo.titulo}
												</Text>
												<Text fontSize="12px" color="gray.500">
													{periodo(informativo)}
												</Text>
											</Flex>
										</Flex>
									);
								})}
							</Grid>
						</Box>
					)}
				</Flex>
			</Flex>

			<DrawerInformativo
				isOpen={drawer.isOpen}
				onClose={drawer.onClose}
				afterClose={buscarInformativos}
				informativo={selecionado}
			/>
		</DashLayout>
	);
};
